'use client';

import { Check, Search, X } from 'lucide-react';
import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { createSafeContext } from '@/lib/create-safe-context';
import { cn, extractText } from '@/lib/utils';

type CommandFilter = (value: string, search: string) => number;

type ItemRecord = {
  id: string;
  value: string;
  disabled: boolean;
  onSelect?: (value: string) => void;
  searchValue: string;
};

type CommandContextValue = {
  search: string;
  setSearch: (next: string) => void;
  filter?: CommandFilter;
  activeId: string | null;
  setActiveId: (id: string | null) => void;
  registerItem: (record: ItemRecord) => () => void;
  updateItem: (id: string, updates: Partial<Omit<ItemRecord, 'id'>>) => void;
  getItemVisibility: (
    item: Pick<ItemRecord, 'value' | 'searchValue'>,
  ) => boolean;
  visibleEnabledItemIds: string[];
  visibleItemCount: number;
  onKeyDown: React.KeyboardEventHandler<HTMLElement>;
  selectActiveItem: () => void;
  listId: string;
};

const [CommandContext, useCommandContext] =
  createSafeContext<CommandContextValue>('Command');

function defaultFilter(value: string, search: string) {
  const haystack = value.toLowerCase();
  const needle = search.toLowerCase().trim();
  if (!needle) return 1;
  return haystack.includes(needle) ? 1 : 0;
}

function composeEventHandlers<E>(
  theirHandler: ((event: E) => void) | undefined,
  ourHandler: (event: E) => void,
) {
  return (event: E) => {
    theirHandler?.(event);
    if ((event as { defaultPrevented?: boolean } | null)?.defaultPrevented)
      return;
    ourHandler(event);
  };
}

function Command({
  className,
  filter,
  children,
  ...props
}: React.ComponentProps<'div'> & { filter?: CommandFilter }) {
  const [search, setSearch] = React.useState('');
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const listId = React.useId();

  const itemsRef = React.useRef(new Map<string, ItemRecord>());
  const [itemsVersion, setItemsVersion] = React.useState(0);

  const registerItem = React.useCallback((record: ItemRecord) => {
    itemsRef.current.set(record.id, record);
    setItemsVersion((v) => v + 1);

    return () => {
      itemsRef.current.delete(record.id);
      setItemsVersion((v) => v + 1);
    };
  }, []);

  const updateItem = React.useCallback(
    (id: string, updates: Partial<Omit<ItemRecord, 'id'>>) => {
      const prev = itemsRef.current.get(id);
      if (!prev) return;
      const next = { ...prev, ...updates };
      itemsRef.current.set(id, next);

      const affectsVisibilityOrOrder =
        prev.value !== next.value ||
        prev.disabled !== next.disabled ||
        prev.searchValue !== next.searchValue;

      if (affectsVisibilityOrOrder) setItemsVersion((v) => v + 1);
    },
    [],
  );

  const effectiveFilter = filter ?? defaultFilter;

  const getItemVisibility = React.useCallback(
    (item: Pick<ItemRecord, 'value' | 'searchValue'>) => {
      if (!search.trim()) return true;
      const score = effectiveFilter(item.searchValue || item.value, search);
      return score > 0;
    },
    [effectiveFilter, search],
  );

  const visibilitySnapshot = React.useMemo(() => {
    void itemsVersion;
    const ids: string[] = [];
    const idSet = new Set<string>();
    let count = 0;

    for (const [id, item] of itemsRef.current) {
      if (!getItemVisibility(item)) continue;
      count += 1;
      if (item.disabled) continue;
      ids.push(id);
      idSet.add(id);
    }
    return { ids, idSet, count };
  }, [getItemVisibility, itemsVersion]);

  const visibleEnabledItemIds = visibilitySnapshot.ids;
  const visibleEnabledItemIdSet = visibilitySnapshot.idSet;
  const visibleItemCount = visibilitySnapshot.count;
  const effectiveActiveId =
    activeId && visibleEnabledItemIdSet.has(activeId)
      ? activeId
      : (visibleEnabledItemIds[0] ?? null);

  const selectActiveItem = React.useCallback(() => {
    if (!effectiveActiveId) return;
    const item = itemsRef.current.get(effectiveActiveId);
    if (
      !item ||
      item.disabled ||
      !visibleEnabledItemIdSet.has(effectiveActiveId)
    )
      return;
    item.onSelect?.(item.value);
  }, [effectiveActiveId, visibleEnabledItemIdSet]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (visibleEnabledItemIds.length === 0) return;

      const index = effectiveActiveId
        ? visibleEnabledItemIds.indexOf(effectiveActiveId)
        : -1;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const next =
          visibleEnabledItemIds[
            Math.min(index + 1, visibleEnabledItemIds.length - 1)
          ];
        setActiveId(next);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        const next = visibleEnabledItemIds[Math.max(index - 1, 0)];
        setActiveId(next);
        return;
      }

      if (event.key === 'Home') {
        event.preventDefault();
        setActiveId(visibleEnabledItemIds[0]);
        return;
      }

      if (event.key === 'End') {
        event.preventDefault();
        setActiveId(visibleEnabledItemIds[visibleEnabledItemIds.length - 1]);
        return;
      }

      if (event.key === 'Enter') {
        if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey)
          return;
        event.preventDefault();
        selectActiveItem();
      }
    },
    [effectiveActiveId, selectActiveItem, visibleEnabledItemIds],
  );

  const contextValue = React.useMemo<CommandContextValue>(
    () => ({
      search,
      setSearch,
      filter,
      activeId: effectiveActiveId,
      setActiveId,
      registerItem,
      updateItem,
      getItemVisibility,
      visibleEnabledItemIds,
      visibleItemCount,
      onKeyDown: handleKeyDown,
      selectActiveItem,
      listId,
    }),
    [
      effectiveActiveId,
      filter,
      getItemVisibility,
      handleKeyDown,
      listId,
      registerItem,
      search,
      selectActiveItem,
      updateItem,
      visibleEnabledItemIds,
      visibleItemCount,
    ],
  );

  return (
    <CommandContext.Provider value={contextValue}>
      <div
        data-slot="command"
        cmdk-root=""
        className={cn(
          'flex size-full flex-col overflow-hidden rounded-xl border border-input bg-background text-foreground shadow-lg',
          className,
        )}
        {...props}
      >
        {children}
        <div
          className="sr-only"
          aria-live="polite"
          aria-atomic="true"
          role="status"
        >
          {search.trim()
            ? visibleItemCount === 0
              ? 'No results found'
              : `${visibleItemCount} result${visibleItemCount === 1 ? '' : 's'} available`
            : ''}
        </div>
      </div>
    </CommandContext.Provider>
  );
}

function CommandDialog({
  title = 'Command Palette',
  description = 'Search for a command to run...',
  children,
  className,
  showCloseButton = false,
  ...props
}: Omit<React.ComponentProps<typeof Dialog>, 'children'> & {
  title?: string;
  description?: string;
  className?: string;
  showCloseButton?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Dialog {...props}>
      <DialogHeader className="sr-only">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <DialogContent
        className={cn(
          'w-[calc(100%-2rem)] max-w-[508px] gap-0 overflow-hidden rounded-xl border border-input bg-background p-0 shadow-lg ring-0 sm:max-w-[508px]',
          className,
        )}
        showCloseButton={showCloseButton}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
}

type CommandInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'defaultValue'
> & {
  /**
   * Render prop for custom input element. Receives props to spread onto your input.
   * @example
   * <CommandInput render={(props) => <MyCustomInput {...props} />} />
   */
  render?: (props: CommandInputRenderProps) => React.ReactElement;
};

type CommandInputRenderProps = {
  'data-slot': string;
  'cmdk-input': string;
  role: 'combobox';
  'aria-expanded': boolean;
  'aria-autocomplete': 'list';
  'aria-controls': string;
  'aria-activedescendant': string | undefined;
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  clearSearch: () => void;
};

function CommandInput({
  className,
  render,
  onChange,
  onKeyDown,
  ...props
}: CommandInputProps) {
  const {
    onKeyDown: onCommandKeyDown,
    search,
    setSearch,
    listId,
    activeId,
  } = useCommandContext();

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(event.target.value);
    },
    [setSearch],
  );
  const clearSearch = React.useCallback(() => {
    setSearch('');
  }, [setSearch]);

  const composedOnChange = composeEventHandlers(onChange, handleChange);
  const composedOnKeyDown = composeEventHandlers(onKeyDown, onCommandKeyDown);

  if (render) {
    return render({
      'data-slot': 'command-input',
      'cmdk-input': '',
      role: 'combobox',
      'aria-expanded': true,
      'aria-autocomplete': 'list',
      'aria-controls': listId,
      'aria-activedescendant': activeId || undefined,
      value: search,
      onChange: composedOnChange,
      onKeyDown: composedOnKeyDown,
      clearSearch,
    });
  }

  return (
    <div
      data-slot="command-input-wrapper"
      cmdk-input-wrapper=""
      className="flex items-center gap-2 border-b border-input px-3"
    >
      <Search
        className="size-4 shrink-0 text-muted-foreground opacity-50"
        aria-hidden="true"
      />
      <input
        data-slot="command-input"
        cmdk-input=""
        role="combobox"
        aria-expanded={true}
        aria-autocomplete="list"
        aria-controls={listId}
        aria-activedescendant={activeId || undefined}
        className={cn(
          'h-11 w-full min-w-0 bg-transparent text-sm leading-5 outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        value={search}
        onChange={composedOnChange}
        onKeyDown={composedOnKeyDown}
        {...props}
      />
      <button
        type="button"
        data-slot="command-input-clear"
        className="flex size-4 shrink-0 items-center justify-center text-muted-foreground opacity-50 outline-none transition-opacity hover:opacity-80 focus-visible:opacity-100 disabled:pointer-events-none"
        disabled={!search}
        aria-label="Clear search"
        onClick={clearSearch}
      >
        <X className="size-4" aria-hidden="true" />
      </button>
    </div>
  );
}

function CommandList({
  className,
  ref,
  ...props
}: React.ComponentProps<'div'>) {
  const { listId, activeId } = useCommandContext();
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const mergedRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      listRef.current = node;
      if (typeof ref === 'function') {
        const cleanup = ref(node);
        if (cleanup) {
          return () => {
            listRef.current = null;
            cleanup();
          };
        }
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );

  React.useEffect(() => {
    const list = listRef.current;
    if (!list || !activeId) return;
    const item = list.ownerDocument.getElementById(activeId);
    if (item && list.contains(item)) {
      const listBounds = list.getBoundingClientRect();
      const itemBounds = item.getBoundingClientRect();
      const scale = list.offsetHeight
        ? listBounds.height / list.offsetHeight
        : 1;
      const top =
        (itemBounds.top - listBounds.top) / scale -
        list.clientTop +
        list.scrollTop;
      const bottom = top + itemBounds.height / scale;
      if (top < list.scrollTop) {
        list.scrollTop = top;
      } else if (bottom > list.scrollTop + list.clientHeight) {
        list.scrollTop = bottom - list.clientHeight;
      }
    }
  }, [activeId]);

  return (
    <div
      ref={mergedRef}
      data-slot="command-list"
      cmdk-list=""
      role="listbox"
      id={listId}
      className={cn(
        'no-scrollbar max-h-72 scroll-py-1 overflow-x-hidden overflow-y-auto p-2 outline-none',
        className,
      )}
      {...props}
    />
  );
}

function CommandEmpty({ className, ...props }: React.ComponentProps<'div'>) {
  const { visibleItemCount } = useCommandContext();
  if (visibleItemCount > 0) return null;

  return (
    <div
      data-slot="command-empty"
      cmdk-empty=""
      className={cn('py-6 text-center text-sm', className)}
      {...props}
    />
  );
}

type CommandGroupProps = React.ComponentProps<'div'> & {
  heading?: React.ReactNode;
};

function CommandGroup({
  className,
  heading,
  children,
  ...props
}: CommandGroupProps) {
  const headingId = React.useId();
  return (
    <section
      data-slot="command-group"
      cmdk-group=""
      aria-labelledby={heading != null ? headingId : undefined}
      className={cn(
        'overflow-hidden text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground',
        className,
      )}
      {...props}
    >
      {heading != null && (
        <div cmdk-group-heading="" id={headingId}>
          {heading}
        </div>
      )}
      {children}
    </section>
  );
}

function CommandSeparator({ className, ...props }: React.ComponentProps<'hr'>) {
  return (
    <hr
      data-slot="command-separator"
      cmdk-separator=""
      className={cn('h-px w-auto bg-border -mx-2', className)}
      {...props}
    />
  );
}

type CommandItemProps = Omit<React.ComponentProps<'div'>, 'onSelect'> & {
  value: string;
  disabled?: boolean;
  onSelect?: (value: string) => void;
  keywords?: string[];
  searchValue?: string;
  showCheckIndicator?: boolean;
};

function CommandItem({
  className,
  children,
  value,
  disabled = false,
  onSelect,
  keywords,
  searchValue: explicitSearchValue,
  showCheckIndicator = true,
  onClick,
  onKeyDown,
  onMouseMove,
  ...props
}: CommandItemProps) {
  const { activeId, getItemVisibility, registerItem, setActiveId, updateItem } =
    useCommandContext();
  const id = React.useId();

  const computedSearchValue = React.useMemo(() => {
    const parts = [
      value,
      keywords?.join(' ') ?? '',
      extractText(children),
    ].filter(Boolean);
    return parts.join(' ');
  }, [children, keywords, value]);
  const searchValue = explicitSearchValue ?? computedSearchValue;

  const visible = getItemVisibility({ value, searchValue });
  const selected = activeId === id;

  React.useLayoutEffect(() => {
    return registerItem({ id, value: '', disabled: false, searchValue: '' });
  }, [id, registerItem]);

  React.useEffect(() => {
    updateItem(id, { value, disabled, onSelect, searchValue });
  }, [disabled, id, onSelect, searchValue, updateItem, value]);

  const handleSelect = React.useCallback(() => {
    if (disabled || !visible) return;
    onSelect?.(value);
  }, [disabled, onSelect, value, visible]);

  return (
    <div
      id={id}
      data-slot="command-item"
      cmdk-item=""
      data-disabled={disabled}
      data-selected={selected}
      role="option"
      aria-selected={selected}
      aria-disabled={disabled}
      hidden={!visible}
      tabIndex={-1}
      className={cn(
        "relative flex cursor-default items-center gap-2 rounded-lg px-2 py-3 text-sm outline-hidden select-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground data-[selected=true]:**:[svg]:text-accent-foreground group/command-item data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      onMouseMove={composeEventHandlers(onMouseMove, () => setActiveId(id))}
      onClick={composeEventHandlers(onClick, () => {
        setActiveId(id);
        handleSelect();
      })}
      onKeyDown={composeEventHandlers(onKeyDown, (event) => {
        if (disabled || !visible) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        setActiveId(id);
        handleSelect();
      })}
      {...props}
    >
      {children}
      {showCheckIndicator && (
        <Check className="ms-auto opacity-0 group-has-[[data-slot=command-shortcut]]/command-item:hidden group-data-[checked=true]/command-item:opacity-100" />
      )}
    </div>
  );
}

function CommandShortcut({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="command-shortcut"
      className={cn(
        'text-muted-foreground group-data-[selected=true]/command-item:text-foreground ms-auto text-xs tracking-widest',
        className,
      )}
      {...props}
    />
  );
}

CommandDialog.displayName = 'Command.Dialog';
CommandInput.displayName = 'Command.Input';
CommandList.displayName = 'Command.List';
CommandEmpty.displayName = 'Command.Empty';
CommandGroup.displayName = 'Command.Group';
CommandSeparator.displayName = 'Command.Separator';
CommandItem.displayName = 'Command.Item';
CommandShortcut.displayName = 'Command.Shortcut';

Command.displayName = 'Command';

const CommandNamespace = Object.assign(Command, {
  Dialog: CommandDialog,
  Input: CommandInput,
  List: CommandList,
  Empty: CommandEmpty,
  Group: CommandGroup,
  Separator: CommandSeparator,
  Item: CommandItem,
  Shortcut: CommandShortcut,
});

export {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandNamespace as Command,
  CommandSeparator,
  CommandShortcut,
};
